import React, { useState } from 'react';
import { useTheme } from '../../lib/theme';
import { patchReceipt } from '../../lib/api';

/**
 * Phase 25.3 — "Mark as personal" switch for the receipt detail.
 *
 * Optimistic PATCH to `/api/receipts/{id}` with `is_personal`. Parent
 * component is expected to re-run `publish-check` (via `onChanged`) so
 * the Publish card reflects the new validation state.
 */
export default function IsPersonalToggle({ receipt, onChanged }) {
  const { colors } = useTheme();
  const [checked, setChecked] = useState(Boolean(receipt?.is_personal));
  const [saving, setSaving] = useState(false);

  const toggle = async () => {
    const next = !checked;
    setChecked(next);
    setSaving(true);
    try {
      await patchReceipt(receipt.id, { is_personal: next });
      onChanged?.(next);
    } catch (e) {
      setChecked(!next);
      console.error('Failed to update is_personal:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="is-personal-toggle"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 12, borderRadius: 10,
        background: colors.surface, border: `1px solid ${colors.border}`,
        gap: 12,
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: colors.text }}>
          Mark as personal
        </div>
        <div style={{ fontSize: 11.5, color: colors.textMuted, marginTop: 2 }}>
          Personal transactions won&apos;t publish to QuickBooks.
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={toggle}
        disabled={saving}
        data-testid="is-personal-switch"
        style={{
          position: 'relative', width: 44, height: 24,
          borderRadius: 999,
          background: checked ? '#F59E0B' : colors.border,
          border: 'none', cursor: saving ? 'wait' : 'pointer',
          transition: 'background 180ms ease',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute', top: 2, left: checked ? 22 : 2,
            width: 20, height: 20, borderRadius: '50%',
            background: 'white', transition: 'left 180ms ease',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}
        />
      </button>
    </div>
  );
}
